// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Ben Richardson <hi@ben.gy>
//
// Assume the reader knows nothing about product-safety regulation. Every term
// used in the interface that a person would not meet in ordinary life is
// defined here and reachable from an ℹ button next to where it is used.

export interface GlossaryEntry {
  term: string;
  definition: string;
}

export const GLOSSARY: Record<string, GlossaryEntry> = {
  recall: {
    term: 'Recall',
    definition:
      'A public notice that a product is unsafe or does not meet a safety law, together with what the supplier will do about it — usually a refund, a repair or a replacement. A recall is about the product, not about any individual item, so it covers everything sold in the affected batch or date range.',
  },
  voluntary: {
    term: 'Voluntary recall',
    definition:
      'Almost every recall in Australia is voluntary: the supplier initiates it and notifies the regulator, which then publishes it. "Voluntary" describes who started it, not how serious it is — it does not mean optional for the supplier once begun.',
  },
  mandatoryStandard: {
    term: 'Mandatory standard',
    definition:
      'A safety rule with the force of law for a particular kind of product — how it must be built, tested or labelled. Products covered include cots, prams, bicycle helmets, toys with small parts, and anything containing button batteries. A great many recalls are for failing a mandatory standard rather than for an injury having occurred.',
  },
  pra: {
    term: 'PRA number',
    definition:
      'The Product Recall Australia reference the ACCC assigns to each recall, in the form 2026/20936. It is the number to quote when contacting a supplier or regulator about a recall.',
  },
  supplier: {
    term: 'Supplier',
    definition:
      'The business responsible for the recall — typically the manufacturer, importer or brand owner. It is the entity the regulator holds accountable, and it is not necessarily the shop you bought the item from.',
  },
  trader: {
    term: 'Trader / retailer',
    definition:
      'Where the product was actually sold — the shops, chains or online marketplaces that stocked it. One supplier\'s recall commonly lists many traders, which is why the same retail chain appears on recalls it did not originate.',
  },
  hazard: {
    term: 'Hazard (inferred)',
    definition:
      'What could go wrong for a person: choking, burns, electric shock, laceration, drowning, chemical exposure and so on. The hazard is the harm; the defect is the fault in the product that could cause it. Important: the ACCC does not publish a hazard code, so the class shown on this site is DERIVED by matching the wording of each notice — it is this site\'s reading, not the regulator\'s. Button batteries are the one exception: those come from the ACCC\'s own category tag. Notices that describe no hazard clearly enough are counted as "Other" rather than guessed at.',
  },
  defect: {
    term: 'Defect',
    definition:
      'The fault in the product itself — a battery compartment that opens, missing warning labels, a component that can break. One defect can create several hazards.',
  },
  buttonBattery: {
    term: 'Button battery',
    definition:
      'A small coin-shaped battery. Swallowed, it can burn through a child\'s oesophagus within hours, and the injury is often fatal or permanent. Australia introduced mandatory standards for button-battery security and labelling in 2022, which is why so many recalls since then are about battery compartments rather than about the battery itself.',
  },
  category: {
    term: 'Product category',
    definition:
      'The ACCC\'s own classification of what kind of thing was recalled, arranged as a tree — for example Kids\' toys → Learning and activity toys. Important caveat: these labels are applied using today\'s taxonomy, including to old recalls, so a change over time is partly a change in how the ACCC classifies things.',
  },
  onSale: {
    term: 'Time on sale',
    definition:
      'The gap between the day a product first went on sale and the day it was recalled, derived from the sale-date range the register publishes. It is not the time the supplier knew about the problem — only how long the product was available before the recall was published.',
  },
  affectedUnits: {
    term: 'Affected units',
    definition:
      'How many individual vehicles a recall campaign covers. The vehicle register publishes this; the consumer-product register does not publish any equivalent, which is why a count of consumer recalls cannot be read as a measure of how many items are affected.',
  },
  campaign: {
    term: 'Campaign number',
    definition:
      'The manufacturer\'s own internal reference for a vehicle recall, distinct from the Australian REC- number. The same global fault often carries one campaign number across many countries.',
  },
  register: {
    term: 'Register',
    definition:
      'The published list of recalls. Australia has two, run separately: consumer products at the ACCC, and vehicles at the Department of Infrastructure. They use different categories and different date meanings, so this site keeps them apart rather than pretending they are one dataset. The exception is a small set of vehicle recalls the ACCC also lists; those carry a REC number and are matched to the vehicle register on every build.',
  },
  expected: {
    term: 'Observed vs expected',
    definition:
      'A way of reading a cross-tab that removes size. "Expected" is how many recalls a category-and-hazard pairing would have if hazards were spread across categories in the same proportions everywhere. Shading the DIFFERENCE, rather than the raw count, stops the biggest category from looking the most dangerous simply for being biggest.',
  },
};

export function glossaryTerm(key: string): GlossaryEntry | null {
  return GLOSSARY[key] ?? null;
}

/** Markup for an inline ℹ trigger. The popover itself is built in components/glossary.ts. */
export function glossaryButton(key: string, label?: string): string {
  const entry = GLOSSARY[key];
  if (!entry) return '';
  const text = label ?? entry.term;
  // The accessible name must CONTAIN the visible label (WCAG 2.2 SC 2.5.3), so
  // speech input users can say what they see. Building it from `text` rather
  // than from the glossary term keeps the two in step at call sites that pass a
  // custom label ("inferred", "affected units").
  return `<button type="button" class="glossary-link" data-term="${key}" aria-label="${text} — show definition of ${entry.term}">${text}<span aria-hidden="true"> ⓘ</span></button>`;
}
